import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useContext, useState, useCallback, useRef } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useNavigate } from 'react-router-dom';

import { AuthContext } from 'src/auth/context';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';

import {
  clearTransactions,
  fetchUserTransactions,
  refreshUserTransactions,
} from 'src/redux/slices/transactions-slice';

import { clientDispatch } from 'src/redux/clientStore';
import { useSelector } from 'src/redux/hooks';
import { useEthereum } from 'src/hooks/useEthereum';
import { useWalletSession } from 'src/hooks/useWalletSession';
import TransactionHistorySkeleton from 'src/features/transactions/TransactionHistorySkeleton';
import TransactionsProtectedView from 'src/features/transactions/TransactionsProtectedView';
import TransactionListDesktop from '../TransactionListDesktop';
import TransactionListMobile from '../TransactionListMobile';

const EmptyState = ({ theme }) => (
  <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, mt: 2 }}>
    <Paper
      elevation={0}
      sx={{
        p: { xs: 4, md: 6 },
        textAlign: 'center',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Iconify
          icon="material-symbols:receipt-long-outline"
          sx={{ fontSize: 72, color: 'text.disabled' }}
        />
      </Box>

      <Typography
        variant="h5"
        sx={{ fontWeight: 800, color: 'text.primary', mb: 1, letterSpacing: '-0.02em' }}
      >
        No transactions yet
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', maxWidth: 420, mx: 'auto', lineHeight: 1.7 }}
      >
        Once you start swapping or sending USD, your history will appear here with status, amounts,
        and settlement details.
      </Typography>
    </Paper>
  </Box>
);

EmptyState.propTypes = {
  theme: PropTypes.object.isRequired,
};

const PAGE_SIZE = 25;

// GLITCH FIXES (vs previous version):
// - Removed the effect that logged wallet users out whenever `connected` was
//   briefly false. On page refresh / iOS app-switch wagmi reports
//   "disconnected" for a moment before it reconnects, which signed people out
//   at random. Disconnecting the wallet from the wallet button still logs a
//   wallet session out (WalletConnectButton-client).
// - Wallet session claim goes through useWalletSession: no claim when the
//   session already matches the wallet, one shared request otherwise, and the
//   AuthContext user is refreshed so the header + live socket room update.
// - The list is fetched once per session identity, not again every time a
//   callback identity changes.
// - Pressing Refresh keeps the list on screen instead of flashing a skeleton.

function getSessionAuthType(user) {
  if (user?.sessionAuthType) return user.sessionAuthType;
  if (user?.isWalletSession) return 'wallet';
  if (user?.isPhoneSession) return 'phone';
  return null;
}

function getSessionKey({ isPhoneSession, userId, walletAddress }) {
  if (isPhoneSession) return `phone:${userId}`;
  if (walletAddress) return `wallet:${walletAddress}`;
  return 'none';
}

function TransactionsView() {
  const [page, setPage] = useState(1);
  const [hasFetched, setHasFetched] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const transactions = useSelector((state) => state?.transactions?.items ?? []);
  const isLoading = useSelector((state) => state?.transactions?.isLoading ?? false);

  const { user, isUserAuthenticated } = useContext(AuthContext);

  const { connected, address, isConnecting, isReconnecting } = useEthereum();
  const { ensureWalletSession, checking: walletSessionChecking } = useWalletSession();

  const hasUserSession = Boolean(isUserAuthenticated && user?._id);

  // Use ACTIVE SESSION auth type, not DB-level user.authType
  const sessionAuthType = getSessionAuthType(user);

  const isPhoneSession = hasUserSession && sessionAuthType === 'phone';
  const isWalletSession = hasUserSession && sessionAuthType === 'wallet';

  const expectedWalletAddress = user?.walletAddress?.toLowerCase?.() || null;
  const connectedWalletAddress = address?.toLowerCase?.() || null;

  const walletAddressMatches = Boolean(
    expectedWalletAddress &&
      connectedWalletAddress &&
      expectedWalletAddress === connectedWalletAddress
  );

  const walletSessionReady =
    isWalletSession && Boolean(connected && connectedWalletAddress) && walletAddressMatches;

  // Allows first-time wallet session claim when wallet is connected but no backend session exists yet
  const canClaimWalletSession = !hasUserSession && Boolean(connected && connectedWalletAddress);

  const canUseTransactions = isPhoneSession || walletSessionReady || canClaimWalletSession;

  const sessionKey = getSessionKey({
    isPhoneSession,
    userId: user?._id,
    walletAddress: connectedWalletAddress,
  });

  // Latest values for the async loader without re-running the effect.
  const latestRef = useRef({});
  latestRef.current = { isPhoneSession, walletSessionReady, address };

  const ensureSessionReady = useCallback(async () => {
    const latest = latestRef.current;

    if (latest.isPhoneSession) return true;
    if (latest.walletSessionReady) return true;

    return ensureWalletSession(latest.address);
  }, [ensureWalletSession]);

  useEffect(() => {
    let cancelled = false;

    const loadTransactions = async () => {
      if (isConnecting || isReconnecting) {
        return;
      }

      if (!canUseTransactions) {
        clientDispatch(clearTransactions());
        setHasFetched(false);
        return;
      }

      setHasFetched(false);

      const sessionReady = await ensureSessionReady();

      if (cancelled) return;

      if (!sessionReady) {
        clientDispatch(clearTransactions());
        setHasFetched(true);
        return;
      }

      try {
        // Session-based: the backend reads req.session.userId / walletAddress.
        await clientDispatch(fetchUserTransactions());
      } catch (error) {
        console.error('[TransactionsView] fetch transactions failed', error);
      } finally {
        if (!cancelled) {
          setHasFetched(true);
        }
      }
    };

    loadTransactions();

    return () => {
      cancelled = true;
    };
  }, [canUseTransactions, isConnecting, isReconnecting, sessionKey, ensureSessionReady]);

  useEffect(() => {
    setPage(1);
  }, [address, user?._id]);

  const handleRefresh = useCallback(async () => {
    const sessionReady = await ensureSessionReady();

    if (!sessionReady) {
      return;
    }

    await clientDispatch(refreshUserTransactions());
  }, [ensureSessionReady]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
  }, [transactions.length]);

  const pagedTxs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return transactions.slice(start, start + PAGE_SIZE);
  }, [transactions, page]);

  const handlePageChange = useCallback((_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const redirect = useCallback(
    (transaction) => {
      navigate(paths.transaction(transaction._id));
    },
    [navigate]
  );

  const hasItems = transactions.length > 0;

  function renderList() {
    if (isMobile) {
      return <TransactionListMobile transactions={pagedTxs} redirect={redirect} />;
    }

    return <TransactionListDesktop transactions={pagedTxs} redirect={redirect} />;
  }

  const renderContent = () => {
    if (isConnecting || isReconnecting) {
      return <TransactionHistorySkeleton />;
    }

    if (!canUseTransactions) {
      return <TransactionsProtectedView />;
    }

    const initialLoading = !hasFetched || (walletSessionChecking && !hasItems);

    if (initialLoading || (isLoading && !hasItems)) {
      return <TransactionHistorySkeleton />;
    }

    if (hasItems) {
      return (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleRefresh}
              disabled={isLoading}
              startIcon={<Iconify icon="material-symbols:refresh-rounded" />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Refresh
            </Button>
          </Stack>

          {renderList()}
        </>
      );
    }

    return <EmptyState theme={theme} />;
  };

  return (
    <Box sx={{ py: { xs: 3, md: 4 }, width: '100%' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>{renderContent()}</Box>

      {transactions.length !== 0 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, transactions.length)}{' '}
            of {transactions.length}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default TransactionsView;
